import { CalendarDays, ChevronRight, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { getDemoAvailability, submitDemoBooking, type OpenSlot } from "../api/demo-booking.js";
import { submitSalesLead, type SalesLeadResponse } from "../api/sales-lead.js";
import { DEMO_BOOKING_URL } from "../booking.js";
import { BookingCalendar } from "../components/BookingCalendar.js";
import { openDialog } from "../dialog.js";
import { formatBookingSelection, groupSlotsByDate, type DateGroup } from "../lib/booking-dates.js";
import { validateLeadForm } from "../validation.js";
import { PRIVACY_DIALOG_ID } from "./PrivacyDialog.js";

type Mode = "demo" | "interest";
type Status = "idle" | "submitting" | "success";
type AvailabilityState = "loading" | "ready" | "empty" | "error";

const VISIBLE_DATE_COUNT = 3;
/** How far out to ask the server for open slots in one call — generous enough that paging
 *  "Later" rarely needs a second request, small enough the response stays tiny. */
const AVAILABILITY_HORIZON_DAYS = 60;

const EMPTY_FIELDS = {
  name: "",
  organizationName: "",
  email: "",
  phone: "",
  message: "",
  website: "",
};

export function DemoSection() {
  const [mode, setMode] = useState<Mode>("demo");
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [status, setStatus] = useState<Status>("idle");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<SalesLeadResponse | { id: string } | null>(null);
  const [bookedSlot, setBookedSlot] = useState<OpenSlot | null>(null);
  // Phone and message are the only optional fields — collapsed by default so the compact mobile
  // card fits one screen; organization stays visible since it's required (validateLeadForm) and
  // hiding a required field behind a disclosure would be a trap, not a convenience.
  const [showMoreFields, setShowMoreFields] = useState(false);

  // GH-901: real availability, fetched once. `slots` is the full fetched set; `page` picks which
  // `VISIBLE_DATE_COUNT`-wide window of *dates with an open slot* is on screen.
  const [availability, setAvailability] = useState<AvailabilityState>("loading");
  const [slots, setSlots] = useState<readonly OpenSlot[]>([]);
  const [page, setPage] = useState(0);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<OpenSlot | null>(null);

  const dateGroups = useMemo(() => groupSlotsByDate(slots), [slots]);
  const visibleGroups = dateGroups.slice(
    page * VISIBLE_DATE_COUNT,
    page * VISIBLE_DATE_COUNT + VISIBLE_DATE_COUNT,
  );
  const canGoEarlier = page > 0;
  const canGoLater = (page + 1) * VISIBLE_DATE_COUNT < dateGroups.length;

  async function loadAvailability() {
    setAvailability("loading");
    const now = new Date();
    const horizon = new Date(now.getTime() + AVAILABILITY_HORIZON_DAYS * 24 * 60 * 60 * 1000);
    const outcome = await getDemoAvailability(now, horizon);
    if (outcome.outcome !== "success") {
      setAvailability("error");
      return;
    }
    setSlots(outcome.slots);
    setPage(0);
    const groups = groupSlotsByDate(outcome.slots);
    if (groups.length === 0) {
      setAvailability("empty");
      setSelectedDateKey(null);
      setSelectedSlot(null);
      return;
    }
    setAvailability("ready");
    selectFirstOf(groups);
  }

  function selectFirstOf(groups: readonly DateGroup[]) {
    const first = groups[0];
    setSelectedDateKey(first?.dateKey ?? null);
    setSelectedSlot(first?.slots[0] ?? null);
  }

  // Fetched once on mount regardless of which tab is active — switching to "Book a demo" then
  // shows real data immediately instead of a loading flash.
  useEffect(() => {
    void loadAvailability();
  }, []);

  function goEarlier() {
    const nextPage = Math.max(0, page - 1);
    setPage(nextPage);
    selectFirstOf(
      dateGroups.slice(
        nextPage * VISIBLE_DATE_COUNT,
        nextPage * VISIBLE_DATE_COUNT + VISIBLE_DATE_COUNT,
      ),
    );
  }
  function goLater() {
    const nextPage = page + 1;
    setPage(nextPage);
    selectFirstOf(
      dateGroups.slice(
        nextPage * VISIBLE_DATE_COUNT,
        nextPage * VISIBLE_DATE_COUNT + VISIBLE_DATE_COUNT,
      ),
    );
  }
  function selectDate(dateKey: string) {
    setSelectedDateKey(dateKey);
    const group = dateGroups.find((g) => g.dateKey === dateKey);
    setSelectedSlot(group?.slots[0] ?? null);
  }

  function updateField(name: keyof typeof EMPTY_FIELDS, value: string) {
    setFields((current) => ({ ...current, [name]: value }));
  }

  function switchMode(next: Mode) {
    setMode(next);
    setFormError(null);
    setFieldErrors({});
  }

  function resetForNewSubmission() {
    setStatus("idle");
    setFields(EMPTY_FIELDS);
    setFieldErrors({});
    setFormError(null);
    setResult(null);
    setBookedSlot(null);
    setShowMoreFields(false);
    void loadAvailability();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    // Honeypot: a real visitor never sees or fills this field. A filled one is a bot — drop the
    // submission silently rather than hitting the API or showing an error either way.
    if (fields.website.trim().length > 0) return;

    const errors = validateLeadForm(fields);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setStatus("submitting");
    const trimmedMessage = fields.message.trim();

    // A real open slot is selected: book it directly — name, email, time, and all, in one
    // request that either holds the slot or answers why it couldn't (GH-901).
    if (mode === "demo" && selectedSlot) {
      const outcome = await submitDemoBooking({
        name: fields.name.trim(),
        email: fields.email.trim(),
        organizationName: fields.organizationName.trim(),
        ...(fields.phone.trim() ? { phone: fields.phone.trim() } : {}),
        ...(trimmedMessage ? { message: trimmedMessage } : {}),
        startsAt: selectedSlot.startsAt,
        endsAt: selectedSlot.endsAt,
      });

      if (outcome.outcome === "success") {
        setStatus("success");
        setResult({ id: outcome.data.leadId });
        setBookedSlot(selectedSlot);
        return;
      }
      setStatus("idle");
      if (outcome.outcome === "slot_taken") {
        setFormError("That time was just booked — please choose another.");
        void loadAvailability();
      } else if (outcome.outcome === "validation_error") {
        setFormError(outcome.message);
      } else if (outcome.outcome === "rate_limited") {
        setFormError("You're submitting a bit fast — please wait a moment and try again.");
      } else {
        setFormError(outcome.message);
      }
      return;
    }

    // "Keep me informed", or "Book a demo" with no open slot right now: the plain lead capture
    // (no appointment) — the owner follows up to find a time by hand.
    const outcome = await submitSalesLead({
      name: fields.name.trim(),
      email: fields.email.trim(),
      organizationName: fields.organizationName.trim(),
      ...(fields.phone.trim() ? { phone: fields.phone.trim() } : {}),
      ...(trimmedMessage ? { message: trimmedMessage } : {}),
    });

    if (outcome.outcome === "success") {
      setStatus("success");
      setResult(outcome.data);
      return;
    }
    setStatus("idle");
    if (outcome.outcome === "validation_error") {
      setFormError(outcome.message);
    } else if (outcome.outcome === "rate_limited") {
      setFormError("You're submitting a bit fast — please wait a moment and try again.");
    } else {
      setFormError(outcome.message);
    }
  }

  const showBooking = mode === "demo" && DEMO_BOOKING_URL.length > 0;
  const hasOpenSlot = availability === "ready" && selectedSlot !== null;

  return (
    <section id="demo" className="demo section">
      <div className="page demo__grid">
        <div className="demo__copy">
          <p className="overline">
            <span className="overline__rule" aria-hidden="true" />
            Let's bring your community together
          </p>
          <h2>
            Your next giving
            <br />
            moment starts
            <br />
            <em>right here.</em>
          </h2>
          <p>
            See the donor experience. Explore the organizer tools. Imagine what's possible for your
            community.
          </p>
          <div className="demo__details">
            <span>
              <CalendarDays size={18} />A personal product walkthrough
            </span>
            <span>
              <CheckCircle2 size={18} />
              Your organization. Your questions.
            </span>
          </div>
        </div>

        <div className="contact-card">
          {status === "success" && result ? (
            <SuccessPanel
              mode={mode}
              email={fields.email}
              preferredTime={bookedSlot ? formatBookingSelection(bookedSlot) : null}
              onReset={resetForNewSubmission}
            />
          ) : (
            <>
              <div
                className="contact-card__tabs"
                role="group"
                aria-label="How would you like to connect?"
              >
                <button
                  type="button"
                  aria-pressed={mode === "demo"}
                  onClick={() => switchMode("demo")}
                >
                  <CalendarDays size={16} />
                  Book a demo
                </button>
                <button
                  type="button"
                  aria-pressed={mode === "interest"}
                  onClick={() => switchMode("interest")}
                >
                  <Mail size={16} />
                  Keep me informed
                </button>
              </div>

              {showBooking ? (
                <div className="contact-card__booking">
                  <h3>Find a time that works.</h3>
                  <p>
                    Choose a time on our booking page. You'll receive the meeting details after
                    booking.
                  </p>
                  <a
                    className="btn btn--primary"
                    href={DEMO_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Choose a demo time
                  </a>
                </div>
              ) : (
                <>
                  <h3>
                    {mode === "demo" ? "Find your giving moment." : "Stay close to what's next."}
                  </h3>
                  <p className="contact-card__intro">
                    {mode === "demo"
                      ? "Choose a time for a personal, 20-minute walkthrough."
                      : "Leave your details to register your interest in GiveHere."}
                  </p>
                  {mode === "demo" && availability === "loading" && (
                    <p className="contact-card__intro">Loading available times…</p>
                  )}
                  {mode === "demo" && availability === "error" && (
                    <p className="contact-card__error" role="alert">
                      Could not load available times. Leave your details below and we'll reach out
                      to schedule.
                    </p>
                  )}
                  {mode === "demo" && availability === "empty" && (
                    <p className="contact-card__intro">
                      No open times right now — leave your details below and we'll reach out to find
                      one.
                    </p>
                  )}
                  {mode === "demo" && availability === "ready" && (
                    <BookingCalendar
                      dateGroups={visibleGroups}
                      selectedDateKey={selectedDateKey}
                      selectedSlot={selectedSlot}
                      canGoEarlier={canGoEarlier}
                      canGoLater={canGoLater}
                      onSelectDate={selectDate}
                      onSelectSlot={setSelectedSlot}
                      onEarlier={goEarlier}
                      onLater={goLater}
                    />
                  )}
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="contact-card__row">
                      <label>
                        Your name
                        <input
                          name="name"
                          autoComplete="name"
                          placeholder="Alex Morgan"
                          maxLength={100}
                          value={fields.name}
                          onChange={(e) => updateField("name", e.target.value)}
                        />
                        {fieldErrors["name"] && (
                          <span className="contact-card__field-error">{fieldErrors["name"]}</span>
                        )}
                      </label>
                      <label>
                        Organization
                        <input
                          name="organization"
                          autoComplete="organization"
                          placeholder="Your community"
                          maxLength={150}
                          value={fields.organizationName}
                          onChange={(e) => updateField("organizationName", e.target.value)}
                        />
                        {fieldErrors["organizationName"] && (
                          <span className="contact-card__field-error">
                            {fieldErrors["organizationName"]}
                          </span>
                        )}
                      </label>
                    </div>
                    <label>
                      Email address
                      <input
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@organization.org"
                        maxLength={254}
                        value={fields.email}
                        onChange={(e) => updateField("email", e.target.value)}
                      />
                      {fieldErrors["email"] && (
                        <span className="contact-card__field-error">{fieldErrors["email"]}</span>
                      )}
                    </label>
                    <button
                      type="button"
                      className="contact-card__more"
                      aria-expanded={showMoreFields}
                      onClick={() => setShowMoreFields((current) => !current)}
                    >
                      <ChevronRight
                        size={13}
                        className="contact-card__more-icon"
                        aria-hidden="true"
                      />
                      {showMoreFields ? "Fewer details" : "Phone, message (optional)"}
                    </button>
                    {showMoreFields && (
                      <>
                        <label>
                          Phone number <span className="contact-card__optional">(optional)</span>
                          <input
                            name="phone"
                            type="tel"
                            autoComplete="tel"
                            placeholder="+1 (555) 000-0000"
                            maxLength={40}
                            value={fields.phone}
                            onChange={(e) => updateField("phone", e.target.value)}
                          />
                        </label>
                        <label>
                          Message <span className="contact-card__optional">(optional)</span>
                          <textarea
                            name="message"
                            placeholder={
                              mode === "demo"
                                ? "Anything you'd like us to know before we talk."
                                : "What would you like to hear about?"
                            }
                            maxLength={2000}
                            value={fields.message}
                            onChange={(e) => updateField("message", e.target.value)}
                          />
                        </label>
                      </>
                    )}
                    <label className="contact-card__honeypot" aria-hidden="true">
                      Website
                      <input
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={fields.website}
                        onChange={(e) => updateField("website", e.target.value)}
                      />
                    </label>
                    {formError && (
                      <p className="contact-card__error" role="alert">
                        {formError}
                      </p>
                    )}
                    <button
                      className="btn btn--primary contact-card__submit"
                      type="submit"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting"
                        ? "Sending…"
                        : mode === "demo"
                          ? hasOpenSlot
                            ? "Book my demo"
                            : "Request a demo"
                          : "Register my interest"}
                    </button>
                    <p className="contact-card__privacy">
                      <ShieldCheck size={14} />
                      We'll use this to follow up about GiveHere.{" "}
                      <button type="button" onClick={() => openDialog(PRIVACY_DIALOG_ID)}>
                        About your information
                      </button>
                    </p>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

interface SuccessPanelProps {
  mode: Mode;
  email: string;
  preferredTime: string | null;
  onReset: () => void;
}

function SuccessPanel({ mode, email, preferredTime, onReset }: SuccessPanelProps) {
  return (
    <div className="contact-card__success" role="status">
      <CheckCircle2 size={44} />
      <p className="overline">Thank you for reaching out</p>
      <h3>{mode === "demo" ? "We'll be in touch." : "You're on the list."}</h3>
      <p>
        {mode === "demo"
          ? preferredTime
            ? `Your demo is held for ${preferredTime} — we'll follow up at ${email} to confirm.`
            : `We received your request and will follow up at ${email} to find a time that works.`
          : `We'll keep you posted at ${email}.`}
      </p>
      {mode === "demo" && (
        <p className="contact-card__fineprint">
          {preferredTime
            ? "This time is held, not yet confirmed — we'll confirm it shortly."
            : "No appointment has been booked yet — we'll reach out to schedule one."}
        </p>
      )}
      <button className="btn btn--primary" onClick={onReset}>
        Back to the form
      </button>
    </div>
  );
}
